import { GENERAL, OFFICE_ACTION, PATENT_APPLICATION, type SessionMode } from "@constants/documentCategories";
import { Dropdown, Input, Option, OptionOnSelectData, SelectionEvents } from "@fluentui/react-components";
import { AppModal } from "@shared/modal/Modal";
import { sessions } from "@strings/index";
import { reportError } from "@utils/observability/reportError";
import * as React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSessionCreation, type SessionFormSubmit } from "../SessionCreationFlow";
import { ALL_SESSION_MODES, isOfficeActionMode } from "@hooks/useSessions";
import { useStyles } from "./NewSessionModal.styles";
import { OfficeActionFields } from "./OfficeActionFields";

const sessionStr = sessions();

type Props = {
  onCancel: () => void;
  onCreated: () => void;
  startingInput?: string;
};

type FormState = {
  name: string;
  mode: SessionMode;
  applicationId: string;
};

export const NewSessionModal = ({ onCancel, onCreated, startingInput = "" }: Props) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { submit, isCreatingSession } = useSessionCreation();

  const [name, setName] = useState(startingInput);
  const [mode, setMode] = useState<SessionMode | null>(null);
  const [applicationId, setApplicationId] = useState("");

  const isOA = isOfficeActionMode(mode);
  const isValid = !!name && !!mode;

  const handleModeChange = (_event: SelectionEvents, data: OptionOnSelectData) => {
    setMode(labelToMode(data.optionText ?? null));
  };

  const handleConfirm = async () => {
    if (!mode || !name) return;
    try {
      await submit(buildSubmitPayload({ name, mode, applicationId }));
      navigate("/chat");
      onCreated();
    } catch (error) {
      // Keep the modal open so the user can retry without retyping. The flow
      // provider already toasts + reports the known createSession failure
      // path; this is a safety net so any unexpected error is still visible.
      reportError("newSessionModal.submit", error, { name, mode });
    }
  };

  const content = (
    <div className={styles.formContainer}>
      <Input
        size="medium"
        className={styles.field}
        placeholder={sessionStr.newProjectIdPlaceholder}
        value={name}
        onChange={(_event, data) => setName(data.value)}
      />
      <Dropdown
        value={mode ? SESSION_MODE_LABELS[mode] : undefined}
        placeholder={sessionStr.newProjectTypePlaceholder}
        onOptionSelect={handleModeChange}
        className={styles.dropdown}
      >
        {ALL_SESSION_MODES.map((m) => (
          <Option key={m} value={m}>
            {SESSION_MODE_LABELS[m]}
          </Option>
        ))}
      </Dropdown>
      {isOA && <OfficeActionFields applicationId={applicationId} onApplicationIdChange={setApplicationId} />}
    </div>
  );

  return (
    <AppModal
      open={true}
      title={sessionStr.newSession}
      content={content}
      confirmText="Confirm"
      cancelText="Cancel"
      onConfirm={handleConfirm}
      onCancel={onCancel}
      modalType="alert"
      confirmButtonProps={{ disabled: !isValid || isCreatingSession }}
    />
  );
};

/**
 * Display labels for each `SessionMode`. The mode itself is the canonical
 * identity; this map exists only to render translated text in the dropdown.
 */
const SESSION_MODE_LABELS: Record<SessionMode, string> = {
  [PATENT_APPLICATION]: sessionStr.patentApplication,
  [OFFICE_ACTION]: sessionStr.officeAction,
  [GENERAL]: sessionStr.general,
};

const labelToMode = (label: string | null): SessionMode | null =>
  ALL_SESSION_MODES.find((m) => SESSION_MODE_LABELS[m] === label) ?? null;

const buildSubmitPayload = ({ name, mode, applicationId }: FormState): SessionFormSubmit => {
  if (isOfficeActionMode(mode)) {
    return { mode, name, oaParams: { applicationId } };
  }
  return { mode, name };
};
